import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactElement, type ReactNode, type Ref } from 'react';
import { toast } from '../../../../design-system/components';
import { track, trackFailed } from '../../../../../../shared-web/track';
import { ApiError } from '../../../auth/api-client';
import { apiErrorCode, wordsRefused, type GroupCheck, type GroupEmoji, type GroupFace, type GroupPoll, type GroupPost, type GroupPostKind, type GroupReaction, type GroupWritingKind } from '../../api';
import { BottomSheet } from '../../components/BottomSheet';
import { Link } from '../../components/Link';
import { PageBack } from '../../components/PageBack';
import { RoomReactionBar } from '../../components/RoomReactions';
import { LIVE_GROUPS_EVENT } from '../../live';
import { BACK_CHEVRON, Face, FriendsBar, dayLabel, fitHeight } from '../friends/friend-parts';
import { groupArt } from './group-art';
import { mascotSvg } from '../../../../../../shared/ember-mascot.js';
import '../../../../../../shared/ember-mascot.css';
import './groups.css';

/**
 * What the Groups pages share (2026-11-03 founder; edge peer-groups.js): a face as a group shows it (Ember's is the
 * flame), when something went up, the live re-read, the mic beside a field, Ember's look before anything goes up, and
 * the words held back with the crisis lines. The pages wear Meet people's paper (`.nf-page`, new-friends.css).
 */

/**
 * Where the post they just put up waits for the group's page to draw it, so it can land there under their eye
 * (2026-11-05, round 4). Read once and cleared; the post goes to the top of the group either way.
 */
export const JUST_POSTED = 'ember.group-just-posted';

/** The writing limits (edge config.js GROUPS; LINE a chain's line). */
export const GROUP_LIMITS = Object.freeze({ QUESTION: 200, BODY: 4000, ANSWER: 2000, LINE: 280 });

/** A Groups page's bar: Back (or the given control) on the left, a title in the middle, a control on the right. */
export function GroupBar({ to, left, title, right }: { to: string; left?: ReactNode; title?: string; right?: ReactNode }): ReactElement {
  return (
    <header className="nf-bar">
      {left ?? <PageBack to={to} className="nf-back" />}
      <div className="nf-bar-mid">{title ? <span className="nf-bar-title gr-bar-title" dir="auto">{title}</span> : null}</div>
      {right ?? <span className="nf-bar-end" aria-hidden />}
    </header>
  );
}

/** A face in a group: a member's room picture, or Ember's flame. */
export function GroupFaceView({ face, size, className = '' }: { face: GroupFace; size: number; className?: string }): ReactElement {
  if (face.ember) {
    return (
      <span className={`gr-ember-face${className ? ` ${className}` : ''}`} style={{ width: size, height: size }} aria-hidden>
        <svg viewBox="0 0 24 24" width={Math.round(size * 0.5)} height={Math.round(size * 0.5)}>
          <path d="M12 3c3 4.5 6 7 6 11a6 6 0 0 1-12 0c0-4 3-6.5 6-11z" fill="#fffaf2" />
        </svg>
      </span>
    );
  }
  return <Face avatar={face.avatar || ''} size={size} className={className} />;
}

/** Faces overlapping in a row, each ringed in white. */
export function FaceStack({ faces, size }: { faces: GroupFace[]; size: number }): ReactElement {
  return (
    <span className="gr-stack" aria-hidden>
      {faces.map((f, i) => <GroupFaceView key={`${f.name}-${i}`} face={f} size={size} className="gr-stack-face" />)}
    </span>
  );
}

/** What a post's header says its writer did. */
function didWhat(post: GroupPost): string {
  if (post.kind === 'poll') return 'started a quick poll';
  if (post.kind === 'chain') return 'started a chain';
  if (post.kind === 'share') return 'shared a story';
  return post.author.ember ? 'asks' : 'asked a question';
}

/**
 * Who put a post up, on its card and its page (mock GroupHome / Post): their face, "Indigo asked a question" (Ember's
 * "Ember asks", "Ember started a chain", "Ember started a quick poll"), and under it when — with `meta` after it
 * ("9 voted · votes are anonymous", "seen by 9"). `more` sits at the end of the row (the post's ···).
 */
export function PostWho({ post, size, meta = '', more = null }: { post: GroupPost; size: number; meta?: string; more?: ReactNode }): ReactElement {
  const line = [upFirst(ago(post.at)), meta].filter(Boolean).join(' · ');
  return (
    <div className="gr-who">
      <GroupFaceView face={post.author} size={size} />
      <span className="gr-who-text">
        <span className="gr-who-line"><strong dir="auto">{post.author.me ? 'You' : post.author.name}</strong> <span className="gr-who-did">{didWhat(post)}</span></span>
        {line && <span className="gr-who-meta">{line}</span>}
      </span>
      {more}
    </div>
  );
}

/** A time at the start of a line: "Yesterday", "Just now", "2h". */
export function upFirst(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** Words cut to `lines` lines on a card, with "See more" under them when there is more (the card opens the post). */
export function Clamp({ text, lines, className }: { text: string; lines: number; className: string }): ReactElement {
  const ref = useRef<HTMLSpanElement>(null);
  const [cut, setCut] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setCut(el.scrollHeight > el.clientHeight + 1);
  }, [text, lines]);
  return (
    <>
      <span ref={ref} className={`gr-clamp ${className}`} style={{ WebkitLineClamp: lines }} dir="auto">{text}</span>
      {cut && <span className="gr-see-more">See more</span>}
    </>
  );
}

/** When something went up: "just now", "40m", "3h", "yesterday", else the day. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const mins = Math.floor(Math.max(0, Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h`;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (new Date(t).toDateString() === yesterday.toDateString()) return 'yesterday';
  return dayLabel(iso);
}

/** A group's own pages read again when the person comes back to the tab, and when the live channel says a group changed. */
export function useGroupsLive(read: (live?: boolean) => void): void {
  const ref = useRef(read);
  ref.current = read;
  useEffect(() => {
    const back = (): void => { if (document.visibilityState === 'visible') ref.current(false); };
    // Only while on screen: a post's page read is a look (its answers count as read, and its writer is not told of them),
    // so a tab left in the background reads again when they come back to it. `live` tells the two apart: coming back to
    // the tab is theirs to see at once, a push while they are reading is theirs to take when they want it.
    const changed = (): void => { if (document.visibilityState === 'visible') ref.current(true); };
    document.addEventListener('visibilitychange', back);
    window.addEventListener(LIVE_GROUPS_EVENT, changed);
    return () => {
      document.removeEventListener('visibilitychange', back);
      window.removeEventListener(LIVE_GROUPS_EVENT, changed);
    };
  }, []);
}

// ───────────────────────── the mic beside a field ─────────────────────────

interface SpeechResults { length: number; [i: number]: { 0: { transcript: string } } }
interface Recognition {
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: { results: SpeechResults }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type RecognitionCtor = new () => Recognition;

function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface Dictation {
  /** The browser can turn speech into words (no mic is drawn where it cannot). */
  supported: boolean;
  /** The field listening now, or null. */
  listening: string | null;
  start: (field: string, typed: string, onText: (text: string) => void) => void;
  stop: () => void;
}

/**
 * Saying it instead of typing it (typing first, the mic second): the browser's own speech recognition, the words
 * landing in the field after what was typed, live as they are heard. Nothing is sent anywhere by us; the field is theirs
 * to read and change before Next.
 */
export function useDictation(): Dictation {
  const rec = useRef<Recognition | null>(null);
  const [listening, setListening] = useState<string | null>(null);
  useEffect(() => () => { rec.current?.abort(); rec.current = null; }, []);

  const start = (field: string, typed: string, onText: (text: string) => void): void => {
    const Ctor = recognitionCtor();
    if (!Ctor) return;
    rec.current?.abort();
    const r = new Ctor();
    r.continuous = true;
    r.interimResults = true;
    const lead = typed && !/\s$/.test(typed) ? `${typed} ` : typed;
    r.onresult = (e) => {
      const said: string[] = [];
      for (let i = 0; i < e.results.length; i += 1) said.push(e.results[i][0].transcript);
      onText(`${lead}${said.join(' ').replace(/\s+/g, ' ').trim()}`);
    };
    r.onerror = (e) => {
      if (e.error === 'aborted' || e.error === 'no-speech') return;
      const denied = e.error === 'not-allowed' || e.error === 'service-not-allowed';
      trackFailed('group_dictation', denied ? 'mic_denied' : e.error || 'unknown');
      toast.error(denied ? 'We need microphone permission to hear you. Typing works just as well.' : 'Couldn’t hear that. Try again, or type it.');
    };
    r.onend = () => {
      if (rec.current !== r) return;
      rec.current = null;
      setListening(null);
    };
    try {
      r.start();
      rec.current = r;
      setListening(field);
    } catch (err) {
      trackFailed('group_dictation', 'start_failed', { error_detail: err instanceof Error ? err.message.slice(0, 120) : '' });
    }
  };
  const stop = (): void => { rec.current?.stop(); };
  return { supported: recognitionCtor() !== null, listening, start, stop };
}

export const MIC = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="2" fill="none" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
  </svg>
);
export const MIC_SMALL = (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
    <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
  </svg>
);

/**
 * One field to write in: its label, a line of help (or a little guide card), the box (display serif for a question), the
 * mic in its corner, and the reason it was refused under it.
 */
export function GroupField({
  id, label, hint, guide, value, onChange, dictation, serif = false, rows, maxLength, placeholder, disabled = false, refused = '', fieldRef,
}: {
  id: string;
  label: string;
  hint?: string;
  guide?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  dictation: Dictation;
  serif?: boolean;
  rows: number;
  maxLength: number;
  placeholder?: string;
  disabled?: boolean;
  refused?: string;
  fieldRef?: Ref<HTMLTextAreaElement>;
}): ReactElement {
  const on = dictation.listening === id;
  const box = useRef<HTMLTextAreaElement | null>(null);
  // Words that arrive by the mic, a draft or a suggestion grow the box as typing does.
  useEffect(() => { fitHeight(box.current); }, [value]);
  const setRefs = (el: HTMLTextAreaElement | null): void => {
    box.current = el;
    if (typeof fieldRef === 'function') fieldRef(el);
    else if (fieldRef) (fieldRef as { current: HTMLTextAreaElement | null }).current = el;
  };
  return (
    <div className="gr-field">
      <label className="gr-field-label" htmlFor={id}>{label}</label>
      {hint && <span className="gr-field-hint" id={`${id}-hint`}>{hint}</span>}
      {guide}
      <div className="gr-field-box">
        <textarea
          id={id}
          ref={setRefs}
          className={`gr-field-input${serif ? ' is-serif' : ''}${on ? ' is-listening' : ''}`}
          value={value}
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={disabled}
          dir="auto"
          aria-describedby={[hint ? `${id}-hint` : '', refused ? `${id}-refused` : ''].filter(Boolean).join(' ') || undefined}
          aria-invalid={Boolean(refused)}
          onChange={(e) => { if (on) dictation.stop(); onChange(e.target.value); }}
        />
        {dictation.supported && !disabled && (
          <button
            type="button"
            className={`gr-mic${on ? ' is-on' : ''}`}
            aria-label={on ? 'Stop listening' : `Say it instead (${label})`}
            aria-pressed={on}
            data-log="group_mic"
            data-log-state={on ? 'stop' : 'start'}
            onClick={() => (on ? dictation.stop() : dictation.start(id, value, onChange))}
          >
            {MIC}
          </button>
        )}
      </div>
      {on && <span className="gr-field-listening" role="status">Listening… tap the mic when you’re done.</span>}
      {refused && <p id={`${id}-refused`} className="gr-refused" role="alert">{refused}</p>}
    </div>
  );
}

// ───────────────────────── when the words don't go up ─────────────────────────

/** Why a post or an answer did not go up: held back for the writer's own sake, one field refused, or a note. */
export type Refusal = { held: string } | { field: string; message: string } | { note: string };

/** `fields` names each field as the page says it ("Your question"). */
export function refusalOf(err: unknown, fields: Record<string, string>): Refusal {
  const code = apiErrorCode(err);
  if (code === 'risk_to_self') {
    const body = err instanceof ApiError ? (err.body as { resources?: string } | null) : null;
    return { held: body?.resources || '' };
  }
  const words = wordsRefused(err, fields);
  if (words) return words.field && fields[words.field] ? { field: words.field, message: words.message } : { note: words.message };
  if (code === 'too_many') return { note: 'That’s a lot for one day. Try again tomorrow.' };
  if (code === 'check_unavailable') return { note: 'Ember couldn’t take a look just now. Try again in a moment.' };
  if (code === 'not_open') return { note: 'This group isn’t open to you anymore.' };
  if (code === 'post_not_found') return { note: 'This post isn’t here anymore.' };
  if (code === 'answer_not_found') return { note: 'That answer isn’t here anymore.' };
  if (code === 'already_added') return { note: 'You’ve already added your line here.' };
  if (code === 'text_too_long') return { note: 'That’s a little long. Try a shorter version?' };
  return { note: 'Couldn’t do that just now. Try again?' };
}

/** Words that carried a risk to the writer: they stay with them, and the lines to call are right here. */
export function HeldNote({ resources, what }: { resources: string; what: 'post' | 'answer' }): ReactElement {
  return (
    <div className="gr-held" role="alert" data-log-seen="group_held" data-log-state={what}>
      <p className="gr-held-title">This stays with you. It won’t be posted.</p>
      {resources && <p className="gr-held-copy">{resources}</p>}
      <Link to="/talk?mode=chat" className="gr-held-link" data-log="group_held_talk" data-log-state={what}>Talk to Ember</Link>
    </div>
  );
}

// ───────────────────────── Ember's look before it goes up ─────────────────────────

/** The parts of `text` that are a change's new words, marked. */
function marked(text: string, nows: string[]): ReactNode[] {
  const ranges: Array<[number, number]> = [];
  for (const now of nows) {
    if (!now) continue;
    for (let i = text.indexOf(now); i >= 0; i = text.indexOf(now, i + now.length)) ranges.push([i, i + now.length]);
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const out: ReactNode[] = [];
  let at = 0;
  for (const [from, to] of ranges) {
    if (from < at) continue;
    if (from > at) out.push(text.slice(at, from));
    out.push(<mark key={from} className="gr-mark">{text.slice(from, to)}</mark>);
    at = to;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

const PERSON = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path d="M5 20c1-3.6 3.8-5.4 7-5.4s6 1.8 7 5.4" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
  </svg>
);
export const HEART = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinejoin="round" />
  </svg>
);

/**
 * While what they wrote is read before it goes up (2026-11-04 founder: 发问题和回复llm check的时候什么都没有感觉是卡住了 要显示一个动效
 * 表达为了群组每个人的安全 我们正在快速检查这段 … 做个可爱放松的小动效): Ember, swaying and changing its look, little hearts drifting
 * up, and why it reads — over the page, from the tap until the words are up or the suggestion is ready.
 */
const CHECK_FACES = ['thinking', 'curious', 'warm'] as const;
export function CheckingVeil({ what }: { what: 'post' | AnswerWord }): ReactElement {
  const html = useMemo(() => mascotSvg({ tag: 'span', width: 76, face: CHECK_FACES[0], faces: CHECK_FACES, lit: true, uid: 'gr-checking' }), []);
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let i = 0;
    const turn = window.setInterval(() => {
      i = (i + 1) % CHECK_FACES.length;
      host.current?.querySelector('.ember-mascot')?.setAttribute('data-face', CHECK_FACES[i]);
    }, 1400);
    return () => window.clearInterval(turn);
  }, []);
  return (
    <div className="gr-checking" role="status" aria-live="polite" data-log-seen="group_checking" data-log-state={what}>
      <div className="gr-checking-card">
        <div className="gr-checking-art" aria-hidden="true">
          <span className="gr-checking-heart is-a">{HEART}</span>
          <span className="gr-checking-heart is-b">{HEART}</span>
          <span className="gr-checking-heart is-c">{HEART}</span>
          <div className="gr-checking-ember" ref={host} dangerouslySetInnerHTML={{ __html: html }} />
        </div>
        <p className="gr-checking-title">Taking a quick look<span className="gr-checking-dots" aria-hidden="true"><i /><i /><i /></span></p>
        <p className="gr-checking-why">To keep the group safe for everyone, each {what} gets a quick read before it goes up. Just a few seconds.</p>
      </div>
    </div>
  );
}

/** Ember, drawn still, wearing one face — beside a word where a group has nothing in it yet. */
export function QuietEmber({ face, width = 64 }: { face: string; width?: number }): ReactElement {
  const html = useMemo(() => mascotSvg({ tag: 'span', width, face, faces: [face], lit: true, uid: `gr-quiet-${face}` }), [face, width]);
  return <span className="gr-quiet-ember" aria-hidden dangerouslySetInnerHTML={{ __html: html }} />;
}

/** What an answer is, as its check page says it: an answer, a reply under one, a chain's line. */
export type AnswerWord = 'answer' | 'reply' | 'line';
const TAKE: Record<AnswerWord, string> = { answer: 'Answer with this version', reply: 'Reply with this version', line: 'Add this version' };

/**
 * "Ember suggests a couple of changes": Ember's flame and "one small thing" on a warm band, the words as the check would
 * have them in one card (its new words marked), what each change was, and theirs to take — or to change themselves,
 * back in the form with the suggested words in it.
 */
export function CheckView({ kind, check, busy, notice, onTake, onEdit, word = 'answer' }: {
  kind: GroupWritingKind;
  check: GroupCheck;
  busy: boolean;
  notice: string;
  onTake: () => void;
  onEdit: () => void;
  /** An answer's kind (its label and button say so). */
  word?: AnswerWord;
}): ReactElement {
  const s = check.suggested ?? {};
  const nows = check.changes.map((c) => c.now);
  const n = check.changes.length;
  const answer = kind === 'answer';
  const parts: Array<{ label: string; text: string; serif: boolean }> = kind === 'question'
    ? [{ label: 'Your question', text: s.question ?? '', serif: true }, { label: 'Background', text: s.body ?? '', serif: false }]
    : kind === 'share' ? [{ label: 'Your post', text: s.body ?? '', serif: false }]
      : [{ label: `Your ${word}`, text: s.text ?? '', serif: false }];
  return (
    <main className="nf-page gr-page" data-log-seen="group_changes" data-log-state={answer ? word : kind}>
      <FriendsBar left={<button type="button" className="nf-iconbtn" aria-label="Back to your words" data-log="group_check_edit" data-log-state="back" onClick={onEdit}>{BACK_CHEVRON}</button>} />
      <section className="gr-band is-cream gr-check-band">
        <BandArt tone="cream" />
        <span className="gr-ember-face gr-check-flame" aria-hidden>
          <svg viewBox="0 0 24 24" width="26" height="26"><path d="M12 3c3 4.5 6 7 6 11a6 6 0 0 1-12 0c0-4 3-6.5 6-11z" fill="#fffaf2" /></svg>
        </span>
        <span className="gr-band-text">
          <span className="gr-band-hand">one small thing</span>
          <h1 className="gr-check-title">{n === 1 ? 'Ember suggests a small change' : n === 2 ? 'Ember suggests a couple of changes' : 'Ember suggests a few changes'}</h1>
        </span>
      </section>
      <div className="gr-check">
        <section className="gr-check-sheet">
          <span className="gr-check-hand">here’s how it would read</span>
          {parts.map((p) => (
            <div className="gr-check-part" key={p.label}>
              <span className="gr-check-label">{p.label}</span>
              <p className={`gr-check-text${p.serif ? ' is-serif' : ''}`} dir="auto">{marked(p.text, nows)}</p>
            </div>
          ))}
        </section>
        <ul className="gr-changes">
          {check.changes.map((c, i) => (
            <li className="gr-change" key={`${c.said}-${i}`}>
              <span className="gr-change-icon">{c.why === 'hurts' ? HEART : PERSON}</span>
              <span className="gr-change-text">
                {c.why === 'hurts' ? 'Softened a line about someone.' : 'Took out a name or detail, so no one can tell who it is.'}
                {' '}You said <s dir="auto">{c.said}</s>.
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="nf-foot">
        {notice && <p className="nf-foot-note is-missing" role="alert">{notice}</p>}
        <button type="button" className="nf-cta is-block" disabled={busy} data-log="group_post" data-log-state={answer ? `${word}_suggested` : 'suggested'} onClick={onTake}>
          {answer ? TAKE[word] : 'Post this version'}
        </button>
        <button type="button" className="nf-text-btn" disabled={busy} data-log="group_check_edit" data-log-state="edit" onClick={onEdit}>Change it myself</button>
      </div>
    </main>
  );
}

// ───────────────────────── the warm bands ─────────────────────────

type Tone = 'peach' | 'sage' | 'cream';
const BAND: Record<Tone, { blob: string; ink: string; dot: string }> = {
  peach: { blob: '#F8D2B6', ink: '#B94B0A', dot: '#E2A23C' },
  sage: { blob: '#D2E1CD', ink: '#24603B', dot: '#E2A23C' },
  cream: { blob: '#EFDCB4', ink: '#8A3A07', dot: '#E2A23C' },
};

/** A band's soft shapes (the hub cards' language): a blob in the corner, a few dots, a little speech mark. */
export function BandArt({ tone, mark = 'none' }: { tone: Tone; mark?: 'question' | 'heart' | 'none' }): ReactElement {
  const c = BAND[tone];
  return (
    <svg className="gr-band-art" viewBox="0 0 358 100" preserveAspectRatio="xMaxYMid slice" aria-hidden>
      <circle cx="330" cy="8" r="58" fill={c.blob} />
      <circle cx="262" cy="90" r="4" fill="#C9B48A" />
      <circle cx="342" cy="88" r="7" fill={c.dot} />
      <circle cx="300" cy="94" r="3" fill={c.dot} />
      {mark !== 'none' && (
        <g>
          <rect x="268" y="34" width="58" height="40" rx="12" fill="#FFFDF8" />
          <path d="M286 74l-6 10 16-10z" fill="#FFFDF8" />
          {mark === 'question'
            ? <><path d="M292 49c0-5 9-5 9 1 0 4-5 4-5 9" stroke={c.ink} strokeWidth="2.4" fill="none" strokeLinecap="round" /><circle cx="296" cy="64" r="1.8" fill={c.ink} /></>
            : <path d="M297 66s-8-5-8-10.5a4.2 4.2 0 0 1 8-2.4 4.2 4.2 0 0 1 8 2.4C305 61 297 66 297 66z" fill={c.ink} />}
        </g>
      )}
    </svg>
  );
}

/** Who they are here, at the top of what they write: their face in the group, "Posting as <name>", and the group. */
export function PostingBand({ me, group, tone, mark }: { me: GroupFace; group: string; tone: Tone; mark: 'question' | 'heart' }): ReactElement {
  return (
    <section className={`gr-band is-${tone}`}>
      <BandArt tone={tone} mark={mark} />
      <GroupFaceView face={me} size={46} className="gr-band-face" />
      <span className="gr-band-text">
        <span className="gr-band-as">Posting as <strong dir="auto">{me.name}</strong></span>
        <span className="gr-band-group" dir="auto">{group}</span>
      </span>
    </section>
  );
}

// ───────────────────────── emoji ─────────────────────────

/**
 * The emoji on a post or an answer, as a count (Groups 2026-11-04, mock GroupHome / Post): its most used few side by
 * side and how many in all. Nothing while there are none.
 */
export function ReactionSummary({ reactions, className = '' }: { reactions: GroupReaction[]; className?: string }): ReactElement | null {
  const total = reactions.reduce((n, r) => n + r.count, 0);
  if (!total) return null;
  const top = [...reactions].sort((a, b) => b.count - a.count).slice(0, 3);
  const mine = reactions.find((r) => r.mine)?.emoji;
  return (
    <span className={`gr-sum${className ? ` ${className}` : ''}`} role="img"
      aria-label={`${total} ${total === 1 ? 'reaction' : 'reactions'}: ${top.map((r) => r.emoji).join(' ')}${mine ? `, yours ${mine}` : ''}`}>
      <span className="gr-sum-emoji">{top.map((r) => r.emoji).join('')}</span>
      {/* Keyed by the count: a new count is a new node, so the bump runs once, on the change itself. */}
      <span className="gr-sum-n" key={total}>{total}</span>
    </span>
  );
}

/**
 * React: opens the quick bar (the first `quick` of the group's set, More for all of it) over the button. One emoji per
 * person per item — picking another replaces theirs, picking theirs takes it off. With theirs on, the button wears it.
 * `look` — the post's action bar, or the small word under an answer.
 */
export function ReactButton({ reactions, emoji, onReact, groupId, what, look }: {
  reactions: GroupReaction[];
  emoji: GroupEmoji | undefined;
  onReact: (emoji: string | null) => void;
  groupId: string;
  what: 'post' | AnswerWord;
  look: 'bar' | 'word';
}): ReactElement | null {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // The one just picked, lifting off the button; it clears itself when the flight is over.
  const [lifting, setLifting] = useState('');
  if (!emoji || emoji.all.length === 0) return null;
  const mine = reactions.find((r) => r.mine)?.emoji ?? null;
  return (
    <>
      <button type="button" className={`${look === 'bar' ? 'gr-act' : 'gr-word'}${mine ? ' is-mine' : ''}`} aria-haspopup="menu"
        aria-label={mine ? `React to this ${what} (yours: ${mine})` : `React to this ${what}`}
        data-log="group_react_open" data-log-state={what} data-log-match-id={groupId} onClick={(e) => setAnchor(e.currentTarget)}>
        {look === 'bar' && (mine ? <span className="gr-act-emoji" aria-hidden>{mine}</span> : SMILE)}
        React
        {lifting && <span className="gr-fly" aria-hidden onAnimationEnd={() => setLifting('')}>{lifting}</span>}
      </button>
      {anchor && (
        <RoomReactionBar anchor={anchor} set={emoji.all} quick={emoji.quick} mine={mine} roomId={groupId}
          label={`React to this ${what}`} moreLog="group_react_more" onClose={() => setAnchor(null)}
          onPick={(e, from) => {
            track('ui.clicked', { name: 'group_react', state: mine === e ? 'removed' : from, match_id: groupId });
            if (mine !== e) setLifting(e);
            onReact(mine === e ? null : e);
          }} />
      )}
    </>
  );
}

// ───────────────────────── a poll ─────────────────────────

const TICK = (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
    <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** A poll's line in its header: how many voted, and that votes are anonymous. */
export function pollMeta(poll: GroupPoll): string {
  return poll.voters > 0 ? `${poll.voters} voted · votes are anonymous` : 'votes are anonymous';
}
/** What a tap does now, under a poll's options. */
export function pollHint(poll: GroupPoll): string {
  return poll.mine !== null ? 'Tap another to change your vote' : 'Tap one to see how the group answered';
}

/**
 * Ember's poll, on its card and its page (2026-11-04): before they vote, its options to tap; once they voted, each
 * option with a quiet bar, its count and share, theirs ticked — a tap on another changes their vote. Votes are anonymous:
 * only counts are ever shown, and only to someone who voted.
 */
export function GroupPollView({ poll, onVote, groupId, where }: {
  poll: GroupPoll;
  onVote: (choice: number) => Promise<void>;
  groupId: string;
  where: 'feed' | 'post';
}): ReactElement {
  // The option tapped, marked while its vote is on the way (the counts come back with it).
  const [picking, setPicking] = useState<number | null>(null);
  const voted = poll.mine !== null;
  const chosen = picking ?? poll.mine;
  const pick = (i: number): void => {
    if (picking !== null || i === poll.mine) return;
    setPicking(i);
    void onVote(i).finally(() => setPicking(null));
  };
  return (
    <div className="gr-poll">
      {poll.options.map((o, i) => {
        const count = o.count ?? 0;
        const pct = poll.voters > 0 ? Math.round((count / poll.voters) * 100) : 0;
        return (
          <button key={i} type="button" className={`gr-poll-opt${voted ? ' is-result' : ''}${i === chosen ? ' is-mine' : ''}`}
            aria-pressed={i === chosen} disabled={picking !== null}
            aria-label={voted ? `${o.text}: ${count} of ${poll.voters}${i === poll.mine ? ', your vote' : ''}` : undefined}
            data-log="group_vote" data-log-state={where} data-log-match-id={groupId} onClick={() => pick(i)}>
            {/* Always drawn, at nothing until they vote: the width it then takes is a change the bar can grow through. */}
            <span className="gr-poll-bar" style={{ width: voted ? `${pct}%` : 0 }} aria-hidden />
            <span className="gr-poll-text" dir="auto">{o.text}</span>
            {i === chosen && <span className="gr-poll-tick">{TICK}</span>}
            {voted && <span className="gr-poll-tally" aria-hidden>{pct}%<span className="gr-poll-count"> · {count}</span></span>}
          </button>
        );
      })}
    </div>
  );
}

// ───────────────────────── a post's ··· ─────────────────────────

/** Report a post (`answer` false) or an answer, reply or line: they won't see it again, and the team takes a look. */
export function ReportSheet({ answer, busy, groupId, onReport, onClose }: {
  answer: boolean;
  busy: boolean;
  groupId: string;
  onReport: () => void;
  onClose: () => void;
}): ReactElement {
  return (
    <BottomSheet
      title={answer ? 'Report this?' : 'Report this post?'}
      description="You won’t see it again, and the Ember team will take a look."
      logSeen="group_report_sheet"
      logSeenAttrs={{ 'data-log-match-id': groupId }}
      onClose={onClose}
    >
      <button type="button" className="ember-btn nf-btn-dark" disabled={busy} data-log="group_report" data-log-state={answer ? 'confirm_answer' : 'confirm'} data-log-match-id={groupId}
        onClick={onReport}>
        Report
      </button>
      <button type="button" className="ember-btn ember-btn-ghost" data-log="group_report" data-log-state="keep" onClick={onClose}>
        Cancel
      </button>
    </BottomSheet>
  );
}

/**
 * Delete their own post, answer, reply or line, for everyone; `under` — answers or replies go with it (`postKind` says
 * which a post has).
 */
export function DeleteSheet({ what, under, postKind, busy, groupId, onDelete, onClose }: {
  what: 'post' | AnswerWord;
  under: boolean;
  postKind: GroupPostKind;
  busy: boolean;
  groupId: string;
  onDelete: () => void;
  onClose: () => void;
}): ReactElement {
  return (
    <BottomSheet
      title={what === 'post' ? 'Delete this post?' : `Delete your ${what}?`}
      description={what === 'line'
        ? 'It’ll be gone for everyone. You can add a new line after.'
        : `It’ll be gone for everyone${under ? `, along with ${what === 'post' ? `its ${postKind === 'question' ? 'answers' : 'replies'}` : 'the replies under it'}` : ''}.`}
      logSeen="group_delete_sheet"
      logSeenAttrs={{ 'data-log-match-id': groupId }}
      onClose={onClose}
    >
      <button type="button" className="ember-btn nf-btn-dark" disabled={busy} data-log="group_delete" data-log-state={`confirm_${what}`} data-log-match-id={groupId}
        onClick={onDelete}>
        Delete
      </button>
      <button type="button" className="ember-btn ember-btn-ghost" data-log="group_delete" data-log-state="keep" onClick={onClose}>
        Keep it
      </button>
    </BottomSheet>
  );
}

// ───────────────────────── a group's look ─────────────────────────

/** The group tints: a ground and its ink. */
const GROUP_TINTS: Array<[string, string]> = [['#E2EDF4', '#3D4A5C'], ['#FBE1CF', '#8A3A07'], ['#E3ECE0', '#24603B'], ['#ECE6F6', '#5B4A8A']];

/** A group's tint — its ground and its ink — the same on every page (from its id). */
export function groupTint(groupId: string): [string, string] {
  let h = 0;
  for (let i = 0; i < groupId.length; i += 1) h = (h * 31 + groupId.charCodeAt(i)) >>> 0;
  return GROUP_TINTS[h % GROUP_TINTS.length];
}

/** A group's mark: a few people, drawn in `ink`. */
export function GroupGlyph({ ink, size = 24 }: { ink: string; size?: number }): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <circle cx="8" cy="8" r="3" stroke={ink} strokeWidth="1.8" fill="none" />
      <circle cx="17" cy="11" r="2.4" stroke={ink} strokeWidth="1.8" fill="none" />
      <path d="M3 19c.8-3 2.8-4.6 5-4.6s4.2 1.6 5 4.6M14 19c.6-2.2 1.9-3.4 3.4-3.4" stroke={ink} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    </svg>
  );
}

/**
 * A group's icon: the group's own drawing (group-art), or — a group with no drawing of its own — its mark in white on
 * its ink. The head of its page, the row in the list, the bar of a post.
 */
export function GroupIcon({ groupId, art, size, className = '' }: { groupId: string; art?: string | null; size: number; className?: string }): ReactElement {
  const drawn = groupArt(art);
  const radius = Math.round(size * 0.28);
  if (drawn) {
    return (
      <span className={`gr-icon is-art${className ? ` ${className}` : ''}`} style={{ width: size, height: size, borderRadius: radius }} aria-hidden>
        {drawn.icon}
      </span>
    );
  }
  const [, ink] = groupTint(groupId);
  return (
    <span className={`gr-icon${className ? ` ${className}` : ''}`} style={{ width: size, height: size, background: ink, borderRadius: radius }} aria-hidden>
      <GroupGlyph ink="#FFFFFF" size={Math.round(size * 0.5)} />
    </span>
  );
}

/**
 * The band over a group's head: the group's own drawing (group-art), or — a group with no drawing of its own — its
 * tinted ground with two soft circles, as every group's head looked before.
 */
export function GroupCover({ groupId, art, children }: { groupId: string; art?: string | null; children?: ReactNode }): ReactElement {
  const drawn = groupArt(art);
  const [ground, ink] = groupTint(groupId);
  return (
    <div className="gr-cover" style={{ background: drawn ? drawn.ground : ground }}>
      <span className="gr-cover-art" aria-hidden>
        {drawn ? drawn.cover : (
          <svg viewBox="0 0 390 128" preserveAspectRatio="xMaxYMid slice" aria-hidden>
            <circle cx="330" cy="10" r="70" fill={ink} fillOpacity="0.08" />
            <circle cx="40" cy="128" r="56" fill={ink} fillOpacity="0.05" />
          </svg>
        )}
      </span>
      {children}
    </div>
  );
}

// ───────────────────────── icons ─────────────────────────

export const LOCK = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" fill="none" />
  </svg>
);

export const MORE = (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
    <circle cx="5.5" cy="12" r="1.7" fill="currentColor" />
    <circle cx="12" cy="12" r="1.7" fill="currentColor" />
    <circle cx="18.5" cy="12" r="1.7" fill="currentColor" />
  </svg>
);

export const CLOSE = (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SMILE = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
    <circle cx="9" cy="10" r="0.6" fill="currentColor" />
    <circle cx="15" cy="10" r="0.6" fill="currentColor" />
  </svg>
);

/** Answer / Reply, in a post's action bar. */
export const SPEECH = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 5h16v11H9l-5 4z" />
  </svg>
);

/** Ask a question. */
export const QUESTION = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.5 2.5 0 0 1 4.8 1c0 1.7-2.3 2-2.3 3.5" />
    <circle cx="12" cy="17" r="0.6" fill="currentColor" />
  </svg>
);

export const CHECK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12l5 5 9-10" />
  </svg>
);

/** Put something up, in the corner of a group (2026-11-05 founder, round 4: 右下角一个大大的加号按钮). */
export const PLUS = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const SEND = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 19V5M5.5 11.5L12 5l6.5 6.5" />
  </svg>
);
